/**
 * Library-wide browser entry point — Stencil allows exactly one `globalScript`, so
 * this file runs on every product's pages. It carries the USLegal analytics contract
 * (MEP-1377, inline below) and the SignNow one (MEP-1371, `installSignNowAnalytics`).
 * A further product needing page-level behaviour should add its own scoped installer
 * here rather than replace these.
 *
 * The blocks library had no page-level script, only components — which is why the
 * `data-send-metric` attributes the USLegal components render were never read by
 * anything. Installing the listeners here rather than from a component keeps them
 * independent of hydration order: the CTA sits above the fold and the footer below it,
 * and binding from either one would lose clicks on the other until that component
 * happened to hydrate. That is the same race that under-counts `CE_getForm_click` on
 * the legacy SignNow render (SLP-2350), and there is no reason to reproduce it here.
 *
 * Running everywhere means nothing here may assume a USLegal page:
 *
 * - the footer resolver refuses to answer outside the USLegal footer root, because
 *   `footer-menu-type-1` is shared with the generic footers and SignNow's;
 * - the scroll metric keys on `.ul-footer-layout-type-1`, which no other product
 *   renders;
 * - `data-send-metric` is opt-in markup, currently emitted by USLegal components only;
 * - the SignNow installer gates every binding on `body.signnow`, and its get-form
 *   binding additionally on the form-page marker.
 *
 * So the USLegal listeners resolve nothing on a SignNow or DocHub page, and the SignNow
 * ones resolve nothing off SignNow. Cost is two click handlers, one submit handler and
 * one scroll handler that unsubscribes as soon as it has fired.
 */

import {
  CLICKSTREAM_ATTRIBUTE,
  METRIC_ATTRIBUTE,
  getMetricParams,
  trackClickstreamEvent,
  trackMetric,
} from './components/product/uslegal/helpers/experiment-metrics';
import { FOOTER_CLICKSTREAM_EVENT, resolveFooterMetric } from './components/product/uslegal/helpers/footer-metrics';
import { installSignNowAnalytics } from './components/product/signnow/helpers/install-analytics';
import { debounce } from './utils/helpers/debounce';

const FOOTER_SELECTOR = '.ul-footer-layout-type-1';
const FOOTER_IN_VIEWPORT_METRIC = 'usl_all_footer_links_in_viewport';

/**
 * Legacy sends `keepalive` only for real anchors, because those navigate before its
 * redirect delay can apply. Buttons and divs go through the components' 700ms delay.
 */
const isNavigatingAnchor = (element: Element): boolean => {
  if (element.tagName !== 'A') return false;
  const href = element.getAttribute('href')?.trim();
  return !!href && href !== '#';
};

const handleClick = (event: MouseEvent): void => {
  const target = event.target as Element | null;
  if (!target?.closest) return;

  const explicit = target.closest(`[${METRIC_ATTRIBUTE}]`);
  const link = explicit ? null : target.closest('a[href]');
  const element = explicit ?? link;
  if (!element) return;

  // An attribute printed by the component always wins; the footer map only fills in
  // the links Herald delivers as bare label/href pairs.
  const name = explicit?.getAttribute(METRIC_ATTRIBUTE) || (link && resolveFooterMetric(link));
  if (!name) return;

  trackMetric(name, {
    attributes: getMetricParams(element),
    keepalive: isNavigatingAnchor(element),
  });

  // The clickstream channel is separate from ABTower and rides the same footer links.
  const clickstreamEvent =
    element.getAttribute(CLICKSTREAM_ATTRIBUTE) || (link ? FOOTER_CLICKSTREAM_EVENT : null);
  if (!clickstreamEvent) return;

  trackClickstreamEvent(clickstreamEvent, {
    url: element.getAttribute('href') ?? '',
    title: element.textContent?.trim() ?? '',
  });
};

/**
 * `usl_all_footer_links_in_viewport` — legacy watches `.layout-responsive__footer`,
 * a class ME does not render, and fires once when the footer's top edge crosses the
 * bottom of the viewport. The threshold is copied so the two arms count the same
 * scroll depth.
 *
 * Legacy's sibling handler for `data-send-metric-when-in-viewport` is deliberately not
 * ported: no USLegal form page uses that attribute on either render, and a permanent
 * scroll-time `querySelectorAll` over a document this large is not worth paying for on
 * an SEO-critical page.
 */
const installFooterInViewportMetric = (): void => {
  const controller = new AbortController();

  const check = () => {
    const footer = document.querySelector(FOOTER_SELECTOR);
    if (!footer || footer.getBoundingClientRect().top - window.innerHeight >= 0) return;

    trackMetric(FOOTER_IN_VIEWPORT_METRIC);
    controller.abort();
  };

  window.addEventListener('scroll', debounce(check, 100), {
    passive: true,
    signal: controller.signal,
  });
  check();
};

export default (): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  document.addEventListener('click', handleClick);
  installFooterInViewportMetric();
  installSignNowAnalytics();
};
